import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';

import LayoutDashboard from 'components/Layout/Dashboard';
import DateRangePicker from 'components/DatePickers/DateRangePicker';
import { getAuditEvents, getMoreAuditEvents, emptyAuditEvents } from 'actions/AuditEvents/Get';
import { search as searchUsers } from 'actions/Users/Search';
import { emptyResults as emptyUsers } from 'actions/Users/Get';
import { ReduxState } from '../../reducers/types';
import AuditEventsTable from 'components/AuditLogs/AuditEventsTable';
import AuditEventDetailDialog from 'components/AuditLogs/AuditEventDetailDialog';

const styles = require('./styles.pcss');

const DEFAULT_LIMIT = 50;

type Filters = {
  identifier: string;
  namespace: string;
  name: string;
  organizationId: string;
  performedBy: string;
  startDate: any;
  endDate: any;
};

type UserOption = {
  id: string;
  label: string;
};

const emptyFilters: Filters = {
  identifier: '',
  namespace: '',
  name: '',
  organizationId: '',
  performedBy: '',
  startDate: null,
  endDate: null,
};

function AuditLogs() {
  const dispatch = useDispatch();
  const events = useSelector((state: ReduxState) => (state as any).auditEvents.events);
  const isLoading = useSelector((state: ReduxState) => (state as any).auditEvents.isLoading);
  const isLoadingMore = useSelector((state: ReduxState) => (state as any).auditEvents.isLoadingMore);
  const hasMore = useSelector((state: ReduxState) => (state as any).auditEvents.hasMore);
  const cursor = useSelector((state: ReduxState) => (state as any).auditEvents.cursor);
  const users = useSelector((state: ReduxState) => (state as any).users.users);
  const isLoadingUsers = useSelector((state: ReduxState) => (state as any).users.isLoading);

  const [filters, setFilters] = React.useState<Filters>({ ...emptyFilters });
  const [selectedEvent, setSelectedEvent] = React.useState<any | null>(null);
  const [selectedUserOption, setSelectedUserOption] = React.useState<UserOption | null>(null);
  const [userInputValue, setUserInputValue] = React.useState('');
  const searchUsersTimeoutRef = React.useRef<number | null>(null);

  const userOptions = React.useMemo(() => {
    return (users || []).map((user: any) => {
      const { name, email } = user
      debugger
      const label = name && email
        ? `${name} - ${email}`
        : user.id;

      return {
        id: user.id,
        label,
      };
    });
  }, [users]);

  const requestUsers = React.useCallback((value: string) => {
    if (value.trim().length < 2) {
      dispatch(emptyUsers() as any);
      return;
    }

    dispatch(searchUsers({ name: value.trim(), limit: 10 }) as any);
  }, [dispatch]);

  const buildParams = (currentFilters: Filters) => {
    const params: any = {
      limit: DEFAULT_LIMIT,
    };
    if (currentFilters.identifier) params.identifier = currentFilters.identifier;
    if (currentFilters.namespace) params.namespace = currentFilters.namespace;
    if (currentFilters.name) params.name = currentFilters.name;
    if (currentFilters.organizationId) params.organizationId = currentFilters.organizationId;
    if (currentFilters.performedBy) params.performedBy = currentFilters.performedBy;
    if (currentFilters.startDate) params.dateFrom = currentFilters.startDate.format('YYYY-MM-DD');
    if (currentFilters.endDate) params.dateTo = currentFilters.endDate.format('YYYY-MM-DD');
    return params;
  };

  React.useEffect(() => {
    dispatch(getAuditEvents(buildParams(emptyFilters)) as any);

    return () => {
      if (searchUsersTimeoutRef.current) {
        window.clearTimeout(searchUsersTimeoutRef.current);
      }
      dispatch(emptyAuditEvents() as any);
      dispatch(emptyUsers() as any);
    };
  }, []);

  const handleUserInputChange = (_event: React.SyntheticEvent, value: string, reason: string) => {
    setUserInputValue(value);

    if (reason === 'clear') {
      setSelectedUserOption(null);
      setFilters((prev) => ({ ...prev, performedBy: '' }));
      dispatch(emptyUsers() as any);
      return;
    }

    if (reason === 'reset') {
      return;
    }

    if (selectedUserOption && value !== selectedUserOption.label) {
      setSelectedUserOption(null);
      setFilters((prev) => ({ ...prev, performedBy: '' }));
    }

    if (searchUsersTimeoutRef.current) {
      window.clearTimeout(searchUsersTimeoutRef.current);
    }

    searchUsersTimeoutRef.current = window.setTimeout(() => {
      requestUsers(value);
    }, 300);
  };

  const handleUserOptionChange = (_event: React.SyntheticEvent, option: UserOption | null) => {
    setSelectedUserOption(option);
    setUserInputValue(option?.label ?? '');
    setFilters((prev) => ({ ...prev, performedBy: option?.id ?? '' }));
  };

  const handleSearch = () => {
    dispatch(getAuditEvents(buildParams(filters)) as any);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleLoadMore = React.useCallback(() => {
    if (!cursor || isLoadingMore) return;
    dispatch(getMoreAuditEvents(buildParams(filters), cursor) as any);
  }, [cursor, isLoadingMore, filters, dispatch]);

  return (
    <LayoutDashboard full>
      <div className={styles.container}>
        <h1 className={styles.title}>Audit Logs</h1>

        <div className={styles.filters} onKeyDown={handleKeyDown}>
          <TextField
            label="Identifier"
            size="small"
            value={filters.identifier}
            onChange={(e) => setFilters((prev) => ({ ...prev, identifier: e.target.value }))}
            className={styles.filterInput}
          />
          <TextField
            label="Namespace"
            size="small"
            value={filters.namespace}
            onChange={(e) => setFilters((prev) => ({ ...prev, namespace: e.target.value }))}
            className={styles.filterInput}
          />
          <TextField
            label="Name"
            size="small"
            value={filters.name}
            onChange={(e) => setFilters((prev) => ({ ...prev, name: e.target.value }))}
            className={styles.filterInput}
          />
          <TextField
            label="Organization ID"
            size="small"
            value={filters.organizationId}
            onChange={(e) => setFilters((prev) => ({ ...prev, organizationId: e.target.value }))}
            className={styles.filterInput}
          />
          <Autocomplete
            size="small"
            options={userOptions}
            value={selectedUserOption}
            inputValue={userInputValue}
            onInputChange={handleUserInputChange}
            onChange={handleUserOptionChange}
            loading={isLoadingUsers}
            filterOptions={(options) => options}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.label}
            noOptionsText={userInputValue.trim().length < 2 ? 'Type at least 2 characters' : 'No users found'}
            className={styles.userFilterInput}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Performed By"
              />
            )}
          />
          <DateRangePicker
            label="Date Range"
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={({ startDate, endDate }: any) => setFilters((prev) => ({ ...prev, startDate, endDate }))}
            className={styles.dateFilterInput}
            error=""
            autoCloseOnSelect
          />
          <DateRangePicker
            label="Date Range"
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={({ startDate, endDate }: any) => setFilters((prev) => ({ ...prev, startDate, endDate }))}
            className={styles.dateFilterInput}
            error=""
            autoCloseOnSelect
          />
          <Button
            variant="contained"
            disableElevation
            onClick={handleSearch}
            disabled={isLoading}
            className={styles.searchButton}
          >
            Search
          </Button>
        </div>

        {isLoading && (
          <div className={styles.loader}>
            <CircularProgress size={32} />
          </div>
        )}

        {!isLoading && events.length === 0 && (
          <p className={styles.empty}>No audit events found.</p>
        )}

        {!isLoading && events.length > 0 && (
          <AuditEventsTable
            events={events}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            onDetailsClick={setSelectedEvent}
          />
        )}
      </div>

      <AuditEventDetailDialog
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />
    </LayoutDashboard>
  );
}

export default AuditLogs;
